// Volca landing. Inicio en dos modos:
//  - "world": vuelo cinematográfico por escenas (técnica scroll-world): videos pre-renderizados
//    cuyo tiempo avanza con el scroll, encadenados sin cortes.
//  - "3d": rodillo en three.js (se carga solo en este modo).
// GSAP ScrollTrigger viene cargado antes como script con defer.
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';
let THREE;

const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
// Celular o pantalla táctil: menos polígonos y menos píxeles para que vaya fluido
const LITE = matchMedia('(pointer: coarse)').matches || innerWidth < 768;
const isMobile = () => matchMedia('(max-width: 767px)').matches;

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const seg = (p, a, b) => clamp((p - a) / (b - a));
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// Recorrido del rodillo sobre la foto (coordenadas normalizadas de la imagen 880x1168).
const IMG_W = 880, IMG_H = 1168;
const PATH = [
  { x: 0.33, y: 0.205 }, { x: 0.67, y: 0.205 },
  { x: 0.64, y: 0.255 }, { x: 0.36, y: 0.255 },
  { x: 0.5, y: 0.33 }, { x: 0.5, y: 0.52 },
  { x: 0.43, y: 0.715, lift: true }, { x: 0.57, y: 0.715 },
];
// Zonas con brillo (zona T).
const SHINE = [
  { x: 0.5, y: 0.228, rx: 0.15, ry: 0.05, a: 0.95, n: 46 },
  { x: 0.5, y: 0.44, rx: 0.026, ry: 0.08, a: 1, n: 18 },
  { x: 0.5, y: 0.494, rx: 0.042, ry: 0.026, a: 1, n: 14 },
  { x: 0.5, y: 0.715, rx: 0.072, ry: 0.032, a: 0.8, n: 20 },
];
const BRUSH = 0.085;
// Rodillo: altura total en unidades (piedra arriba = +0.5, base = -2.72) y offset para centrarlo
const BODY_H = 3.22;
const BODY_MID = 1.11; // diámetro del borrado, fracción del ancho de la foto

document.querySelectorAll('.volca').forEach(initPage);

function initPage(root) {
  root.classList.add('is-js');
  initReveal(root);
  initVideos(root);
  const stage = root.querySelector('[data-stage]');
  if (!stage) return;
  if (stage.dataset.mode === 'world') { initWorld(root, stage); return; }
  if (!canWebGL()) { snapshotFallback(root); return; }
  import(THREE_URL).then((m) => { THREE = m; if (!initScene(root, stage)) snapshotFallback(root); }).catch(() => snapshotFallback(root));
}

/* ---------- Vuelo por escenas (scroll-world) ----------
   Cada escena es un clip que empieza exactamente en la imagen de esa escena y termina en la
   siguiente, así las uniones coinciden. El scroll decide el tiempo de cada clip; en cada unión
   hay un fundido corto que tapa la pequeña diferencia del render. */
function initWorld(root, stage) {
  const scenes = [...stage.querySelectorAll('[data-w-scene]')];
  const copies = [...stage.querySelectorAll('[data-w-copy]')];
  const N = scenes.length;
  if (!N) return;
  root.classList.add('is-world');
  const portrait = () => innerWidth < 768 || innerHeight > innerWidth * 1.15;
  const FADE = 0.07; // fracción final de cada clip donde entra el siguiente
  const items = scenes.map((el) => ({ el, img: el.querySelector('img'), video: null, ready: false, cur: 0, target: 0, src: null,
    frames: +el.dataset.framesM || 0, sheetUrls: (el.dataset.sheetsM || '').split('|').filter(Boolean), sheets: null, canvas: null, drawn: -1 }));
  const state = { target: 0, p: 0 };

  /* Celular: en vez de video, hojas de cuadros dibujadas en un canvas. Safari en iPhone no
     mueve un video con el scroll si hay ahorro de batería o "Reducir movimiento"; las imágenes
     siempre funcionan. Cada hoja trae 12 cuadros (3 columnas x 4 filas de 540x960). */
  const CELL_W = 540, CELL_H = 960, COLS = 3, PER = 12;
  const useSeq = () => portrait() && items.every((it) => it.frames && it.sheetUrls.length);
  const loadSeq = (it) => {
    if (it.sheets) return;
    it.sheets = it.sheetUrls.map((url) => {
      const im = new Image();
      im.src = url;
      // Decodifica fuera del hilo principal antes de usarla: así no traba el scroll
      const done = () => { if (it.sheets) { im.ok = true; it.drawn = -1; } };
      (im.decode ? im.decode() : Promise.reject()).then(done, () => { im.onload = done; if (im.complete && im.naturalWidth) done(); });
      return im;
    });
    if (!it.canvas) {
      const c = document.createElement('canvas');
      c.width = CELL_W; c.height = CELL_H; // tamaño fijo: el CSS lo ajusta (object-fit), nunca se redimensiona al hacer scroll
      c.setAttribute('aria-hidden', 'true');
      it.el.appendChild(c); it.canvas = c; it.ctx = c.getContext('2d', { alpha: false });
    }
  };
  // Libera la memoria de escenas lejanas (teléfonos con poca RAM)
  const unloadSeq = (it) => {
    if (!it.sheets) return;
    it.sheets.forEach((im) => { im.removeAttribute('src'); });
    it.sheets = null; it.drawn = -1;
  };
  const cellAt = (it, f) => {
    const im = it.sheets[Math.floor(f / PER)];
    return im && im.ok ? im : null;
  };
  const blit = (it, im, f) => {
    const cell = f % PER;
    it.ctx.drawImage(im, (cell % COLS) * CELL_W, Math.floor(cell / COLS) * CELL_H, CELL_W, CELL_H, 0, 0, CELL_W, CELL_H);
  };
  const drawSeq = (it, t) => {
    if (!it.sheets) return;
    const x = clamp(t) * (it.frames - 1);
    let f = Math.floor(x);
    // Mezcla entre dos cuadros vecinos: con 10 cuadros/seg se ve fluido como video
    const mix = Math.round((x - f) * 8) / 8;
    const key = f + mix;
    if (key === it.drawn) return;
    let a = cellAt(it, f);
    if (!a) {
      // Si esa hoja no ha llegado, usa el cuadro cargado más cercano
      let best = -1, dist = Infinity;
      it.sheets.forEach((im, s) => {
        if (!im.ok) return;
        const g = clamp(f, s * PER, Math.min(it.frames - 1, s * PER + PER - 1));
        if (Math.abs(g - f) < dist) { dist = Math.abs(g - f); best = g; }
      });
      if (best < 0) return;
      a = cellAt(it, best); f = best;
      it.ctx.globalAlpha = 1; blit(it, a, f); it.drawn = -2;
    } else {
      it.ctx.globalAlpha = 1; blit(it, a, f);
      const b = mix > 0 && f + 1 < it.frames ? cellAt(it, f + 1) : null;
      if (b) { it.ctx.globalAlpha = mix; blit(it, b, f + 1); it.ctx.globalAlpha = 1; }
      it.drawn = key;
    }
    it.el.classList.add('has-clip', 'is-seq');
  };

  const load = (it) => {
    if (useSeq()) { loadSeq(it); return; }
    if (reduceMotion) return;
    const want = portrait() ? (it.el.dataset.srcM || it.el.dataset.src) : it.el.dataset.src;
    if (it.src === want) return;
    it.src = want;
    if (!it.video) {
      const v = document.createElement('video');
      v.muted = true; v.playsInline = true; v.preload = 'auto';
      v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('aria-hidden', 'true');
      v.addEventListener('loadedmetadata', () => { it.ready = true; });
      v.addEventListener('seeked', () => it.el.classList.add('has-clip'));
      // Si falta la versión vertical, usa la horizontal
      v.addEventListener('error', () => { const d = it.el.dataset.src; if (it.src !== d) { it.src = d; v.src = d; } });
      it.el.appendChild(v); it.video = v;
    }
    it.ready = false; it.el.classList.remove('has-clip');
    it.video.src = want;
  };
  const setPosters = () => items.forEach((it) => {
    const src = portrait() ? (it.el.dataset.posterM || it.el.dataset.poster) : it.el.dataset.poster;
    if (it.img.getAttribute('src') !== src) it.img.src = src;
    it.img.onerror = () => { if (it.img.getAttribute('src') !== it.el.dataset.poster) it.img.src = it.el.dataset.poster; };
  });
  setPosters();

  // iOS: un video silenciado que nunca se reprodujo no pinta el cuadro buscado; se "despierta" al primer toque
  const prime = () => items.forEach((it) => { const v = it.video; if (!v) return; const pr = v.play(); if (pr && pr.then) pr.then(() => v.pause()).catch(() => {}); });
  addEventListener('touchstart', prime, { once: true, passive: true });

  let scroller = getScroller(stage);
  // Altura del encabezado del tema sobre el vuelo: el primer texto se sube para que no se corte
  const setOffset = () => {
    const off = clamp(stage.getBoundingClientRect().top - viewTopOf(scroller) + scrollTopOf(scroller), 0, innerHeight * 0.25);
    root.style.setProperty('--v-offset', `${Math.round(off)}px`);
  };
  setOffset();
  addEventListener('load', setOffset);
  if (window.gsap && window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    let st;
    const make = () => { st?.kill(); st = ScrollTrigger.create({ trigger: stage, scroller, start: 'top top', end: 'bottom bottom', onUpdate: (self) => { state.target = self.progress; } }); };
    make();
    let w = innerWidth;
    addEventListener('resize', () => {
      const sc = getScroller(stage); if (sc !== scroller) { scroller = sc; make(); }
      if (innerWidth !== w) { w = innerWidth; setPosters(); if (!useSeq()) items.forEach((it) => it.el.classList.remove('is-seq')); items.forEach((it) => it.video && load(it)); }
    });
  }

  const smooth = (x) => { x = clamp(x); return x * x * (3 - 2 * x); };
  const frame = () => {
    state.p = reduceMotion ? state.target : lerp(state.p, state.target, LITE ? 0.3 : 0.14);
    const pos = clamp(state.p, 0, 0.9999) * N;
    const i = Math.floor(pos), local = pos - i;
    const seq = useSeq();
    items.forEach((it, k) => {
      if (Math.abs(k - i) <= 1 || (seq && k === i + 2 && local > 0.5)) load(it);
      else if (seq && Math.abs(k - i) > 2) unloadSeq(it);
      let op = 0;
      if (k === i) op = 1;
      else if (k === i + 1 && local > 1 - FADE) op = smooth((local - (1 - FADE)) / FADE);
      it.el.style.opacity = op;
      it.el.style.zIndex = k === i ? 1 : 2;
      it.target = k < i ? 1 : k === i ? local : 0;
      if (seq) {
        if (it.canvas && (op > 0 || Math.abs(it.cur - it.target) > 0.002)) {
          it.cur = reduceMotion ? it.target : lerp(it.cur, it.target, 0.6);
          drawSeq(it, it.cur);
        }
        return;
      }
      const v = it.video;
      if (v && it.ready && !v.seeking && (op > 0 || Math.abs(it.cur - it.target) > 0.002)) {
        it.cur = reduceMotion ? it.target : lerp(it.cur, it.target, 0.35);
        const t = clamp(it.cur, 0, 0.999) * (v.duration || 1);
        if (Math.abs(v.currentTime - t) > (LITE ? 0.03 : 0.012)) { try { v.currentTime = t; } catch (e) {} }
      }
      if (!it.el.classList.contains('has-clip') && !reduceMotion) it.img.style.transform = `scale(${(1.02 + local * 0.06 * (k === i)).toFixed(3)})`;
    });
    // Textos: cada uno aparece en su escena; el primero ya está visible al llegar, el último se queda
    copies.forEach((c, k) => {
      const center = k === 0 ? 0 : k === N - 1 ? N - 0.5 : k;
      let o;
      if (k === 0) o = smooth(1 - pos / 0.55);
      else if (k === N - 1) o = smooth((pos - (N - 1.15)) / 0.3);
      else o = smooth(1 - Math.abs(pos - center) / 0.5);
      c.style.opacity = o.toFixed(3);
      c.style.transform = reduceMotion ? 'none' : `translateY(${((center - pos) * 22).toFixed(1)}px)`;
      c.style.visibility = o > 0.01 ? 'visible' : 'hidden';
    });
    stage.style.setProperty('--w-progress', state.p.toFixed(4));
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

/* ---------- Utilidades de página ---------- */
function initReveal(root) {
  const items = root.querySelectorAll('.v-reveal');
  if (reduceMotion || !('IntersectionObserver' in window)) { items.forEach((el) => el.classList.add('is-in')); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { rootMargin: '0px 0px -12% 0px' });
  items.forEach((el, i) => { el.style.transitionDelay = `${(i % 4) * 70}ms`; io.observe(el); });
}

// Los videos se reproducen solo mientras están en pantalla (ahorra datos y batería en celular)
function initVideos(root) {
  const frames = root.querySelectorAll('.v-video iframe[data-src]');
  const lazy = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      target.src = target.dataset.src; target.removeAttribute('data-src'); lazy.unobserve(target);
    });
  }, { rootMargin: '300px 300px' });
  frames.forEach((f) => lazy.observe(f));
  initReviewModal(root);
  const vids = root.querySelectorAll('.v-video video, .v-offer-media__video, .v-steps-media__video');
  if (!vids.length) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach(({ target: v, isIntersecting }) => {
      if (isIntersecting && !reduceMotion) v.play().catch(() => {});
      else v.pause();
    });
  }, { threshold: 0.4 });
  vids.forEach((v) => { v.removeAttribute('autoplay'); v.muted = true; io.observe(v); });
}

// Visor de reseñas: abre el video en grande con sus controles (play/pausa, volumen, barra de tiempo).
// Las zonas del reproductor que llevan a TikTok (autora, logo, botones laterales) quedan tapadas.
function initReviewModal(root) {
  const modal = root.querySelector('[data-tt-modal]');
  const buttons = root.querySelectorAll('[data-tt-open]');
  if (!modal || !buttons.length) return;
  const player = modal.querySelector('[data-tt-player]');
  const credit = modal.querySelector('[data-tt-credit]');
  const closeBtn = modal.querySelector('.v-modal__close');
  let lastFocus = null;
  let scroller = null;
  const open = (btn) => {
    lastFocus = btn;
    player.src = `https://www.tiktok.com/player/v1/${btn.dataset.ttOpen}?autoplay=1&controls=1&progress_bar=1&play_button=1&volume_control=1&timestamp=1&fullscreen_button=0&music_info=0&description=0&rel=0&native_context_menu=0&closed_caption=0`;
    credit.textContent = `Video de @${btn.dataset.ttHandle} en TikTok`;
    modal.hidden = false;
    scroller = getScroller(root);
    (scroller === window ? document.documentElement : scroller).style.overflow = 'hidden';
    void modal.offsetWidth; // fuerza el estilo inicial para que la transición corra
    modal.classList.add('is-open');
    closeBtn.focus();
  };
  const close = () => {
    modal.classList.remove('is-open');
    player.src = 'about:blank'; // detiene el video
    if (scroller) (scroller === window ? document.documentElement : scroller).style.overflow = '';
    setTimeout(() => { modal.hidden = true; }, 220);
    lastFocus?.focus();
  };
  buttons.forEach((b) => b.addEventListener('click', () => open(b)));
  modal.querySelectorAll('[data-tt-close]').forEach((el) => el.addEventListener('click', close));
  addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); });
}

// Algunos temas (Horizon en escritorio) hacen scroll dentro de un contenedor y no en la ventana
function getScroller(el) {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
  }
  return window;
}
function scrollTopOf(sc) { return sc === window ? scrollY : sc.scrollTop; }
function viewTopOf(sc) { return sc === window ? 0 : sc.getBoundingClientRect().top; }

function canWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGL2RenderingContext && c.getContext('webgl2'));
  } catch { return false; }
}

/* ---------- Texturas ---------- */
function rng(seed) {
  return () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

function stoneTextures() {
  const w = LITE ? 512 : 1024, h = LITE ? 256 : 512, rand = rng(7);
  const color = document.createElement('canvas'); color.width = w; color.height = h;
  const bump = document.createElement('canvas'); bump.width = w; bump.height = h;
  const c = color.getContext('2d'), b = bump.getContext('2d');
  c.fillStyle = '#d9ae8e'; c.fillRect(0, 0, w, h);
  b.fillStyle = '#9a9a9a'; b.fillRect(0, 0, w, h);
  // Manchas minerales
  for (let i = 0; i < 900; i++) {
    const x = rand() * w, y = rand() * h, r = 4 + rand() * 26, l = rand();
    c.fillStyle = l > 0.5 ? `rgba(240,205,180,${0.06 + rand() * 0.1})` : `rgba(160,110,80,${0.05 + rand() * 0.08})`;
    c.beginPath(); c.ellipse(x, y, r, r * (0.6 + rand() * 0.6), rand() * 3, 0, Math.PI * 2); c.fill();
  }
  // Poros
  for (let i = 0; i < 2600; i++) {
    const x = rand() * w, y = rand() * h, r = 0.6 + Math.pow(rand(), 3) * 3.6;
    c.fillStyle = `rgba(120,78,55,${0.35 + rand() * 0.4})`;
    c.beginPath(); c.ellipse(x, y, r, r * (0.7 + rand() * 0.5), rand() * 3, 0, Math.PI * 2); c.fill();
    b.fillStyle = `rgba(0,0,0,${0.6 + rand() * 0.4})`;
    b.beginPath(); b.ellipse(x, y, r, r * (0.7 + rand() * 0.5), 0, 0, Math.PI * 2); b.fill();
  }
  // Destellos
  for (let i = 0; i < 1400; i++) {
    c.fillStyle = `rgba(250,225,205,${0.2 + rand() * 0.3})`;
    c.fillRect(rand() * w, rand() * h, 1.2, 1.2);
  }
  const map = new THREE.CanvasTexture(color); map.colorSpace = THREE.SRGBColorSpace; map.anisotropy = 8;
  const bumpMap = new THREE.CanvasTexture(bump);
  return { map, bumpMap };
}

function studioEnv(renderer) {
  const env = new THREE.Scene();
  const room = new THREE.Mesh(new THREE.BoxGeometry(20, 12, 20), new THREE.MeshBasicMaterial({ color: 0x6e7470, side: THREE.BackSide }));
  env.add(room);
  const panel = (w, h, x, y, z, ry, intensity) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(1, 1, 1).multiplyScalar(intensity) }));
    m.position.set(x, y, z); m.rotation.y = ry; m.lookAt(0, 0, 0); env.add(m);
  };
  panel(8, 4, -6, 5, 4, 0, 6);
  panel(4, 8, 8, 1, 2, 0, 3.5);
  panel(10, 2, 0, -5, 8, 0, 1.6);
  panel(3, 6, -4, 0, -9, 0, 2.4);
  const pm = new THREE.PMREMGenerator(renderer);
  const tex = pm.fromScene(env, 0.035).texture;
  pm.dispose();
  return tex;
}

/* ---------- Modelo del rodillo (pivote en el centro de la piedra) ----------
   Proporciones tomadas de la foto del producto: cuerpo negro mate tipo reloj de arena,
   cuello escalonado, cabezal cilíndrico y piedra volcánica color durazno. */
function buildRoller() {
  const group = new THREE.Group();
  const body = new THREE.MeshPhysicalMaterial({ color: 0x18191a, roughness: 0.62, clearcoat: 0.25, clearcoatRoughness: 0.55, sheen: 0.3, sheenColor: 0x4a4d50, side: THREE.DoubleSide });
  const capMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.08, clearcoat: 1, clearcoatRoughness: 0.05, transparent: true, opacity: 0.22, depthWrite: false });
  const { map, bumpMap } = stoneTextures();
  const stoneMat = new THREE.MeshStandardMaterial({ map, bumpMap, bumpScale: 1.6, roughness: 0.96, color: 0xffffff });

  const lathe = (pts, mat, seg = LITE ? 64 : 128) => new THREE.Mesh(new THREE.LatheGeometry(pts.map(([r, y]) => new THREE.Vector2(r, y)), seg), mat);

  const stone = new THREE.Mesh(new THREE.SphereGeometry(0.5, LITE ? 48 : 96, LITE ? 32 : 64), stoneMat);
  group.add(stone);

  // Cabezal + cuello + cuerpo en una sola pieza torneada
  group.add(lathe([
    [0.47, 0.2], [0.49, 0.3], [0.53, 0.335], [0.568, 0.32], [0.585, 0.27],
    [0.585, -0.14], [0.578, -0.155], [0.586, -0.165], [0.586, -0.18],
    [0.66, -0.19], [0.68, -0.205], [0.68, -0.262], [0.705, -0.27], [0.712, -0.285], [0.712, -0.31],
    [0.68, -0.37], [0.6, -0.59], [0.53, -0.85], [0.475, -1.15], [0.465, -1.35], [0.478, -1.75],
    [0.53, -2.05], [0.585, -2.35], [0.628, -2.56], [0.632, -2.63], [0.61, -2.665],
    [0.545, -2.675], [0.54, -2.72], [0, -2.72],
  ], body));

  // Tapa transparente
  const cap = lathe([[0.605, -0.17], [0.612, 0.3], [0.59, 0.5], [0.5, 0.6], [0.3, 0.635], [0, 0.64]], capMat);
  cap.renderOrder = 2;
  group.add(cap);

  return { group, stone, stoneMat, cap, capMat };
}

/* ---------- Escena ---------- */
function initScene(root, stage) {
  const canvas = stage.querySelector('[data-gl]');
  const faceEl = stage.querySelector('[data-face]');
  const shineCanvas = stage.querySelector('[data-shine]');
  const panels = [...stage.querySelectorAll('[data-panel]')];
  const sticky = stage.querySelector('.v-stage__sticky');

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: !LITE, alpha: true, powerPreference: 'high-performance' });
  } catch { return null; }
  root.classList.add('is-3d');

  renderer.setPixelRatio(Math.min(devicePixelRatio, LITE ? 1.5 : 1.75));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.environment = studioEnv(renderer);
  const key = new THREE.DirectionalLight(0xffffff, 1.6); key.position.set(-4, 6, 8); scene.add(key);
  const rim = new THREE.DirectionalLight(0xffe2d6, 1.1); rim.position.set(6, 2, -6); scene.add(rim);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8a918c, 0.5));

  const FOV = 28, CAM_Z = 16;
  const camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 100);
  camera.position.set(0, 0, CAM_Z);

  const rig = new THREE.Group();
  const model = new THREE.Group();
  const roller = buildRoller();
  model.add(roller.group);
  rig.add(model);
  scene.add(rig);

  const stoneDry = new THREE.Color(0xffffff), stoneOily = new THREE.Color(0xa88b78);

  // Layout en píxeles del contenedor sticky
  let scroller = getScroller(stage);
  const L = { w: 1, h: 1, face: { cx: 0, cy: 0, w: 1, h: 1 }, halfH: 1, halfW: 1, off: 0, heroTop: 0 };
  const heroPanel = panels[0];
  const shineCtx = shineCanvas.getContext('2d');
  let speckles = [];

  function layout() {
    const r = sticky.getBoundingClientRect();
    L.w = r.width; L.h = r.height;
    renderer.setSize(L.w, L.h, false);
    camera.aspect = L.w / L.h; camera.updateProjectionMatrix();
    L.halfH = CAM_Z * Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    L.halfW = L.halfH * camera.aspect;
    // Altura del header del tema sobre la escena: se compensa en el primer pantallazo
    scroller = getScroller(stage);
    L.off = clamp(stage.getBoundingClientRect().top - viewTopOf(scroller) + scrollTopOf(scroller), 0, L.h * 0.25);
    root.style.setProperty('--v-offset', `${Math.round(L.off)}px`);
    L.heroTop = heroPanel.getBoundingClientRect().top - r.top;
    const fr = faceEl.getBoundingClientRect();
    L.face = { cx: fr.left + fr.width / 2 - r.left, cy: fr.top + fr.height / 2 - r.top, w: faceEl.offsetWidth, h: faceEl.offsetHeight };
    const dpr = Math.min(devicePixelRatio, LITE ? 1.5 : 2);
    shineCanvas.width = Math.round(L.face.w * dpr); shineCanvas.height = Math.round(L.face.h * dpr);
    buildSpeckles();
    lastShine = -1;
  }

  // Píxel de pantalla -> mundo en el plano z=0
  const toWorld = (sx, sy) => new THREE.Vector3(((sx / L.w) * 2 - 1) * L.halfW, (-(sy / L.h) * 2 + 1) * L.halfH, 0);
  const pxPerUnit = () => L.h / (2 * L.halfH);
  const facePx = (pt) => ({ x: L.face.cx + (pt.x - 0.5) * L.face.w, y: L.face.cy + (pt.y - 0.5) * L.face.h });

  // Longitud del recorrido (en unidades de imagen)
  const segs = [];
  let total = 0;
  for (let i = 1; i < PATH.length; i++) {
    const a = PATH[i - 1], b = PATH[i];
    const len = Math.hypot((b.x - a.x) * IMG_W, (b.y - a.y) * IMG_H);
    segs.push({ a, b, len, start: total, lift: !!b.lift });
    total += len;
  }
  function pointAt(t) {
    const d = t * total;
    for (const s of segs) {
      if (d <= s.start + s.len) {
        const k = s.len ? (d - s.start) / s.len : 0;
        return { x: lerp(s.a.x, s.b.x, k), y: lerp(s.a.y, s.b.y, k), lift: s.lift ? Math.sin(k * Math.PI) : 0 };
      }
    }
    const e = PATH[PATH.length - 1];
    return { x: e.x, y: e.y, lift: 0 };
  }

  function buildSpeckles() {
    const rand = rng(11);
    speckles = [];
    SHINE.forEach((z) => {
      for (let i = 0; i < z.n; i++) {
        const ang = rand() * Math.PI * 2, rad = Math.sqrt(rand()) * 0.95;
        speckles.push({ x: z.x + Math.cos(ang) * rad * z.rx * 0.8, y: z.y + Math.sin(ang) * rad * z.ry * 0.8, r: 0.5 + Math.pow(rand(), 3) * 1.3, a: 0.25 + rand() * 0.4 });
      }
    });
  }

  let lastShine = -1;
  function drawShine(t) {
    if (Math.abs(t - lastShine) < 0.0005) return;
    lastShine = t;
    const W = shineCanvas.width, H = shineCanvas.height, c = shineCtx;
    c.globalCompositeOperation = 'source-over';
    c.filter = 'none';
    c.clearRect(0, 0, W, H);
    // Brillo graso: halo amplio + núcleo especular + destellos finos
    const s = W / IMG_W;
    SHINE.forEach((z) => {
      [[1.1, 0.6], [0.55, 1], [0.25, 1]].forEach(([size, k]) => {
        c.save();
        c.translate(z.x * W, z.y * H);
        c.scale(z.rx * W * size, z.ry * H * size);
        const g = c.createRadialGradient(0, -0.15, 0, 0, 0, 1);
        g.addColorStop(0, `rgba(255,250,242,${z.a * k})`);
        g.addColorStop(0.5, `rgba(255,246,232,${z.a * k * 0.5})`);
        g.addColorStop(1, 'rgba(255,246,232,0)');
        c.fillStyle = g;
        c.fillRect(-1, -1, 2, 2);
        c.restore();
      });
    });
    c.filter = `blur(${(0.7 * s).toFixed(2)}px)`;
    speckles.forEach((p) => {
      c.fillStyle = `rgba(255,255,255,${p.a})`;
      c.beginPath(); c.arc(p.x * W, p.y * H, p.r * s * 1.4, 0, Math.PI * 2); c.fill();
    });
    if (t <= 0) return;
    // Borrar por donde ya pasó la piedra
    c.globalCompositeOperation = 'destination-out';
    c.filter = LITE ? 'none' : `blur(${Math.round(6 * s)}px)`;
    c.lineCap = 'round'; c.lineJoin = 'round';
    c.lineWidth = BRUSH * W;
    c.strokeStyle = '#000';
    const d = t * total;
    for (const sg of segs) {
      if (d <= sg.start) break;
      if (sg.lift) continue;
      const k = Math.min(1, (d - sg.start) / sg.len);
      c.beginPath();
      c.moveTo(sg.a.x * W, sg.a.y * H);
      c.lineTo(lerp(sg.a.x, sg.b.x, k) * W, lerp(sg.a.y, sg.b.y, k) * H);
      c.stroke();
    }
  }

  // Estado
  const state = { target: 0, p: 0, intro: reduceMotion ? 1 : 0, mx: 0, my: 0, tmx: 0, tmy: 0, spin: 0 };
  const api = { onProgress: null };
  const introStart = performance.now() + 150;

  if (window.gsap && window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    let st;
    const makeTrigger = () => {
      st?.kill();
      st = ScrollTrigger.create({ trigger: stage, scroller, start: 'top top', end: 'bottom bottom', onUpdate: (self) => { state.target = self.progress; } });
    };
    makeTrigger();
    // Si al cambiar el tamaño de la ventana cambia el contenedor de scroll (celular a escritorio), se vuelve a crear
    addEventListener('resize', () => { const sc = getScroller(stage); if (sc !== scroller) { scroller = sc; makeTrigger(); } });
  }

  if (!reduceMotion) {
    addEventListener('pointermove', (e) => { state.tmx = (e.clientX / innerWidth) * 2 - 1; state.tmy = (e.clientY / innerHeight) * 2 - 1; }, { passive: true });
  }

  const v3 = (x, y, z) => new THREE.Vector3(x, y, z);
  const tmpE = new THREE.Euler();

  function frame(dt) {
    state.p = reduceMotion ? state.target : lerp(state.p, state.target, 1 - Math.exp(-dt * (LITE ? 11 : 8)));
    if (Math.abs(state.p - state.target) < 0.0002) state.p = state.target;
    const p = state.p;
    state.mx = lerp(state.mx, state.tmx, 1 - Math.exp(-dt * 4));
    state.my = lerp(state.my, state.tmy, 1 - Math.exp(-dt * 4));
    api.onProgress?.(state.target);
    if (state.intro < 1) state.intro = clamp((performance.now() - introStart) / 1400);

    const mobile = isMobile();
    const kOpen = easeInOut(seg(p, 0.1, 0.28));
    const kApproach = easeInOut(seg(p, 0.3, 0.42));
    const kRoll = seg(p, 0.42, 0.84);
    const kExit = easeInOut(seg(p, 0.86, 0.96));

    // Poses
    const heroPx = mobile ? { x: L.w / 2, y: (L.heroTop + 64) / 2 + 10 } : { x: L.face.cx, y: L.face.cy - L.off / 2 + L.h * 0.02 };
    const heroLen = mobile ? Math.min(L.h * 0.46, (L.heroTop - 64) * 0.86) : L.h * 0.6;
    const heroScale = heroLen / BODY_H / pxPerUnit();
    const stoneScale = (BRUSH * 1.25 * L.face.w) / pxPerUnit();

    // Hero -> abierto
    let pos = toWorld(heroPx.x, heroPx.y);
    let rot = v3(0.14, 0, -0.32);
    let scale = heroScale;
    let pivot = BODY_MID; // offset del modelo: 1.7 = centrado en el cuerpo, 0 = centrado en la piedra
    const openRot = v3(0.42, 0, 0.18);
    rot.lerp(openRot, kOpen);
    scale = lerp(heroScale, heroScale * 1.12, kOpen);
    pos.y += lerp(0, -0.25 * heroScale, kOpen);

    // Abierto -> sobre el rostro
    const pt = pointAt(easeInOut(kRoll) * 0.999 + (kRoll >= 1 ? 0.001 : 0));
    const ptPx = facePx(pt);
    const facePos = toWorld(ptPx.x, ptPx.y);
    facePos.z = 0.35 * stoneScale + pt.lift * 0.9;
    const faceRot = v3(-0.7, 0.3, 0.95);
    pos.lerp(facePos, kApproach);
    rot.lerp(faceRot, kApproach);
    scale = lerp(scale, stoneScale * (1 + pt.lift * 0.12), kApproach);
    pivot = lerp(pivot, 0, kApproach);

    // Rostro -> pose final junto a la cara
    const restPx = mobile
      ? { x: L.face.cx + L.face.w * 0.38, y: L.face.cy + L.face.h * 0.22 }
      : { x: L.face.cx + L.face.w * 0.4, y: L.face.cy + L.face.h * 0.2 };
    const restPos = toWorld(restPx.x, restPx.y);
    pos.lerp(restPos, kExit);
    rot.lerp(v3(0.1, 0, -0.32), kExit);
    scale = lerp(scale, heroScale * (mobile ? 0.5 : 0.5), kExit);
    pivot = lerp(pivot, BODY_MID, kExit);

    // Entrada + giro en reposo + parallax del puntero
    const heroWeight = 1 - Math.max(kOpen, kApproach);
    if (!reduceMotion && p < 0.3) state.spin += dt * 0.35 * heroWeight;
    const introK = 1 - Math.pow(2, -10 * state.intro) * (state.intro < 1 ? 1 : 0);
    rig.position.copy(pos);
    rig.position.y -= (1 - introK) * 1.2;
    tmpE.set(
      rot.x + state.my * 0.12 * heroWeight,
      rot.y + state.spin * heroWeight + (1 - introK) * -1.6 + state.mx * 0.25 * heroWeight,
      rot.z,
    );
    rig.rotation.copy(tmpE);
    rig.scale.setScalar(scale * lerp(0.82, 1, introK));
    model.position.y = pivot;

    // Tapa
    roller.cap.position.y = kOpen * 1.8;
    roller.cap.position.x = kOpen * 0.7;
    roller.cap.rotation.z = -kOpen * 0.8;
    roller.capMat.opacity = 0.22 * (1 - seg(kOpen, 0.45, 1));
    roller.cap.visible = roller.capMat.opacity > 0.003;
    // La piedra rueda y se oscurece al absorber
    roller.stone.rotation.x = -easeInOut(kRoll) * total / IMG_W / (BRUSH * 0.625);
    roller.stoneMat.color.copy(stoneDry).lerp(stoneOily, easeInOut(kRoll));
    roller.stoneMat.roughness = lerp(0.95, 0.72, easeInOut(kRoll));

    // Rostro
    const faceK = easeOut(seg(p, 0.27, 0.4));
    faceEl.style.opacity = faceK.toFixed(3);
    faceEl.style.scale = lerp(0.94, 1, faceK).toFixed(4);
    drawShine(easeInOut(kRoll));

    // Paneles de texto
    const windows = [[-1, 0, 0.06, 0.1], [0.11, 0.14, 0.26, 0.29], [0.33, 0.37, 0.8, 0.84], [0.86, 0.9, 2, 2]];
    panels.forEach((el, i) => {
      const [a, b, c, d] = windows[i];
      const o = Math.min(seg(p, a, b), 1 - seg(p, c, d));
      el.style.opacity = o.toFixed(3);
      el.style.transform = `translateY(${((1 - o) * (p < (a + d) / 2 ? 18 : -18)).toFixed(1)}px)`;
      el.style.visibility = o > 0.01 ? 'visible' : 'hidden';
      el.classList.toggle('is-on', o > 0.5);
    });

    // Si nada cambió desde el último cuadro, no se vuelve a dibujar (ahorra batería en celular)
    const sig = `${p.toFixed(5)}|${introK.toFixed(4)}|${rig.rotation.y.toFixed(4)}|${rig.rotation.x.toFixed(4)}|${L.w}x${L.h}`;
    if (sig !== lastSig) { renderer.render(scene, camera); lastSig = sig; }
  }
  let lastSig = '';

  // Bucle sólo cuando la escena está en pantalla
  let running = false, last = 0, raf = 0;
  const loop = (now) => {
    const dt = Math.min(0.05, (now - last) / 1000 || 0.016);
    last = now;
    frame(dt);
    raf = requestAnimationFrame(loop);
  };
  new IntersectionObserver(([e]) => {
    if (e.isIntersecting && !running) { running = true; last = performance.now(); raf = requestAnimationFrame(loop); }
    else if (!e.isIntersecting && running) { running = false; cancelAnimationFrame(raf); }
  }).observe(stage);

  const faceImg = stage.querySelector('[data-face-img]');
  layout();
  if (!faceImg.complete) faceImg.addEventListener('load', layout, { once: true });
  let rt;
  addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { layout(); window.ScrollTrigger?.refresh(); }, 120); });

  snapshot(root, renderer, scene, rig, model, roller);
  frame(0.016);
  return api;
}

// Foto del producto para el cierre cuando la tienda aún no tiene imagen del producto
function snapshot(root, renderer, scene, rig, model, roller) {
  const img = root.querySelector('img[data-snapshot]');
  if (!img) return;
  const prev = { size: renderer.getSize(new THREE.Vector2()), pr: renderer.getPixelRatio() };
  const cam = new THREE.PerspectiveCamera(26, 600 / 750, 0.1, 100);
  cam.position.set(0, 0, 14);
  rig.position.set(0, 0, 0); rig.rotation.set(0.15, 0.5, -0.22); rig.scale.setScalar(1.45);
  model.position.y = BODY_MID; roller.cap.visible = true; roller.capMat.opacity = 0.22;
  renderer.setPixelRatio(2);
  renderer.setSize(600, 750, false);
  renderer.render(scene, cam);
  img.src = renderer.domElement.toDataURL('image/png');
  renderer.setPixelRatio(prev.pr);
  renderer.setSize(prev.size.x, prev.size.y, false);
}

function snapshotFallback(root) {
  const img = root.querySelector('img[data-snapshot]');
  if (img) img.closest('.v-final__media').hidden = true;
}
